import { useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { useAuthor } from '@/hooks/useAuthor';
import { useStreamingQuery } from '@/hooks/useStreamingQuery';
import { MagazineCard } from '@/components/MagazineCard';
import { EbookCard } from '@/components/EbookCard';
import { CopyButton } from '@/components/CopyButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, BookOpen, BookMarked, Globe, ExternalLink, Loader2 } from 'lucide-react';
import { KIND_MAGAZINE, KIND_EBOOK } from '@/lib/pressstrKinds';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const author = useAuthor(pubkey);
  const meta = author.data?.metadata;
  const npub = nip19.npubEncode(pubkey);

  const magFilters  = useMemo(() => [{ kinds: [KIND_MAGAZINE], authors: [pubkey], limit: 200 }], [pubkey]);
  const bookFilters = useMemo(() => [{ kinds: [KIND_EBOOK],    authors: [pubkey], limit: 200 }], [pubkey]);

  const { events: magazines, isLoading: magsLoading, isStreaming: magsStreaming } =
    useStreamingQuery(magFilters,  ['profile-magazines', pubkey]);
  const { events: ebooks, isLoading: ebooksLoading, isStreaming: ebooksStreaming } =
    useStreamingQuery(bookFilters, ['profile-ebooks', pubkey]);

  const displayName = meta?.display_name || meta?.name || npub.slice(0, 16) + '…';

  useSeoMeta({
    title: `${displayName} — PressStr`,
    description: meta?.about ?? `Publications by ${displayName} on PressStr`,
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Banner */}
      <div className="relative h-32 sm:h-48 bg-gradient-to-br from-primary/20 via-primary/10 to-background overflow-hidden">
        {meta?.banner && (
          <img src={meta.banner} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-3 left-3">
          <Button variant="ghost" size="sm" asChild className="bg-black/30 hover:bg-black/50 text-white hover:text-white backdrop-blur-sm">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
          </Button>
        </div>
      </div>

      {/* Profile info */}
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Avatar row — overlaps banner */}
        <div className="flex items-end gap-4 -mt-10 sm:-mt-14 mb-4">
          <Avatar className="w-20 h-20 sm:w-28 sm:h-28 border-4 border-background shadow-lg shrink-0">
            <AvatarImage src={meta?.picture} />
            <AvatarFallback className="text-2xl">{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="pb-1 min-w-0 flex-1" />
        </div>

        {/* Name + metadata */}
        <div className="space-y-3 mb-6">
          {author.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-72" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold break-words">{displayName}</h1>
                {meta?.bot && <Badge variant="secondary">Bot</Badge>}
              </div>
              {meta?.nip05 && (
                <p className="text-sm text-primary font-mono">{meta.nip05}</p>
              )}
              {meta?.about && (
                <p className="text-sm text-muted-foreground whitespace-pre-line break-words max-w-xl">
                  {meta.about}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {meta?.website && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={meta.website} target="_blank" rel="noopener noreferrer">
                      <Globe className="w-3.5 h-3.5 mr-1.5" />
                      {new URL(meta.website).hostname}
                      <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                    </a>
                  </Button>
                )}
                <CopyButton value={npub} label="Copy npub" tooltip="Copy npub" variant="outline" className="font-mono text-xs" />
                <span className="font-mono text-xs text-muted-foreground hidden sm:inline truncate max-w-[14rem]">
                  {npub.slice(0, 20)}…
                </span>
              </div>
            </>
          )}
        </div>

        {/* Publications tabs */}
        <Tabs defaultValue="magazines">
          <TabsList className="mb-5 w-full sm:w-auto">
            <TabsTrigger value="magazines" className="flex-1 sm:flex-none gap-1.5">
              <BookOpen className="w-4 h-4" />
              Magazines
              {magsStreaming
                ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground hidden sm:inline-block" />
                : <span className="text-xs text-muted-foreground hidden sm:inline">({magazines.length})</span>
              }
            </TabsTrigger>
            <TabsTrigger value="ebooks" className="flex-1 sm:flex-none gap-1.5">
              <BookMarked className="w-4 h-4" />
              Ebooks
              {ebooksStreaming
                ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground hidden sm:inline-block" />
                : <span className="text-xs text-muted-foreground hidden sm:inline">({ebooks.length})</span>
              }
            </TabsTrigger>
          </TabsList>

          <TabsContent value="magazines">
            {magsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="aspect-square rounded-lg w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : magazines.length === 0 && !magsStreaming ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No magazines published yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {magazines.map((e: NostrEvent) => (
                  <MagazineCard key={e.id} event={e} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ebooks">
            {ebooksLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="aspect-[2/3] rounded-lg w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : ebooks.length === 0 && !ebooksStreaming ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BookMarked className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No ebooks published yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                {ebooks.map((e: NostrEvent) => (
                  <EbookCard key={e.id} event={e} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
